<template>
    <div class="modal-overlay" @click.stop>
        <div class="modal-container" @click.stop>
            <div class="modal-header">
                <h2 class="modal-title">
                    <span class="message-icon">📱</span>
                    문자 발송
                </h2>
                <button class="close-button" @click="closeModal">
                    <Close :size="24" />
                </button>
            </div>

            <div class="modal-body">
                <!-- * 수신자 정보 -->
                <div class="recipient-section">
                    <h3 class="section-title">수신자 정보</h3>
                    <div class="recipient-card">
                        <div class="recipient-avatar">👤</div>
                        <div class="recipient-info">
                            <div class="recipient-name">{{ isTestUser ? '테스트' : recipient.name }}</div>
                            <div class="recipient-phone">
                                <div>{{ isTestUser ? '010-0000-0000' : recipient.phone }}</div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- * 발송 방법 선택 -->
                <div class="send-method-section">
                    <h3 class="section-title">발송 방법</h3>
                    <div class="method-buttons">
                        <button class="method-button" :class="{ active: sendMethod === 'sms' }"
                            @click="sendMethod = 'sms'">
                            <div class="method-icon">📱</div>
                            <div class="method-text">문자메시지</div>
                        </button>
                        <button class="method-button" :class="{ active: sendMethod === 'kakao' }"
                            @click="sendMethod = 'kakao'">
                            <div class="method-icon">💬</div>
                            <div class="method-text">카카오톡</div>
                        </button>
                    </div>
                </div>

                <!-- * 메시지 입력 -->
                <div class="message-section">
                    <h3 class="section-title">
                        메시지 내용
                        <span class="character-count">{{ message.length }}/{{ maxLength }}</span>
                    </h3>
                    <textarea v-model="message" :maxlength="maxLength" placeholder="함께 전송할 메시지를 입력하세요..."
                        class="message-textarea" />
                    <div class="message-templates">
                        <button v-for="template in messageTemplates" :key="template.id" class="template-button"
                            @click="applyTemplate(template.content)">
                            {{ template.name }}
                        </button>
                    </div>
                </div>

                <!-- * 문자 전송내역 조회 -->
                <div class="history-section">
                    <div class="history-header">
                        <h3 class="section-title">전송내역</h3>
                    </div>

                    <!-- 전송내역 목록 -->
                    <div v-if="isLoadingHistory" class="history-loading">
                        <div class="loading-spinner-large">⏳</div>
                        <p>전송내역을 불러오는 중...</p>
                    </div>

                    <div v-else-if="sendHistory.length === 0" class="history-empty">
                        <div class="empty-icon">📭</div>
                        <p>조회된 전송내역이 없습니다.</p>
                    </div>

                    <div v-else class="history-list">
                        <div v-for="(item, index) in sendHistory" :key="index" class="history-item">
                            <div class="history-item-header">
                                <div class="history-item-type">{{ getItemTypeLabel(item.type) }}</div>
                                <div class="history-item-status" :class="getStatusClass(item.state)">
                                    {{ getStatusLabel(item.state) }}
                                </div>
                            </div>
                            <div class="history-item-body">
                                <div class="history-item-info">
                                    <span class="info-label">수신자:</span>
                                    <span class="info-value">{{ item.receiveName }}</span>
                                </div>
                                <div class="history-item-info">
                                    <span class="info-label">수신번호:</span>
                                    <span class="info-value">{{ item.receiveNum }}</span>
                                </div>
                                <div class="history-item-info">
                                    <span class="info-label">전송일시:</span>
                                    <span class="info-value">{{ formatDateTime(item.sendDT) }}</span>
                                </div>
                                <div class="history-item-info">
                                    <span class="info-label">전송내용:</span>
                                    <span class="info-value content-preview">{{ truncateContent(item.content) }}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="modal-footer">
                <div class="footer-info">
                    <span class="info-icon">ℹ️</span>
                    {{ sendMethod === 'kakao' ? '알림톡 발송: 20원' : '문자단문(45자): 35원 / 문자장문(1,000자): 70원' }}
                </div>
                <div class="footer-buttons">
                    <button class="cancel-button" @click="closeModal">취소</button>
                    <button class="send-button" :class="{ kakao: sendMethod === 'kakao', sms: sendMethod === 'sms' }"
                        @click="sendMessage" :disabled="!canSend">
                        <Send :size="20" />
                        {{ sendMethod === 'kakao' ? '카카오톡 발송' : '문자 발송' }}
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import Close from "vue-material-design-icons/Close.vue";
import Send from "vue-material-design-icons/Send.vue";

export default {
    name: 'SmsSendModal',

    components: {
        Close,
        Send
    },

    props: {
        recipient: {
            type: Object,
            required: true,
            validator: (value) => {
                return value.name && (value.phone1 || value.phone2);
            }
        }
    },

    emits: ['close', 'send'],

    data() {
        return {
            sendMethod: 'sms',
            message: '',
            maxLength: 2000,
            isLoadingHistory: false,
            sendHistory: [],
            messageTemplates: [
                {
                    id: 1,
                    name: '기본 인사',
                    content: '안녕하세요 {name}님 {company}입니다.\n보낼 문자를 자유롭게 입력해주세요.\n감사합니다.'
                },
                { id: 2, name: '긴급', content: '[긴급] 확인이 필요한 문서를 전송드립니다. 확인 후 회신 부탁드립니다.' },
            ]
        };
    },

    computed: {
        isTestUser() {
            try {
                const userInfo = JSON.parse(localStorage.getItem('userInfo'));
                return userInfo && userInfo.id === 'test';
            } catch (error) {
                return false;
            }
        },

        canSend() {
            return this.message.trim().length > 0;
        }
    },

    mounted() {
        // 모달이 열리면 자동으로 전송내역 조회
        this.loadSendHistory();
    },

    methods: {
        async loadSendHistory() {
            try {
                if (!this.$globalState.company_bnum || !this.$globalState.popbill_id) {
                    this.sendHistory = [];
                    return;
                }

                this.isLoadingHistory = true;

                // 최근 30일 기준으로 조회
                const today = new Date();
                const thirtyDaysAgo = new Date();
                thirtyDaysAgo.setDate(today.getDate() - 30);

                const SDate = this.formatDateYYYYMMDD(thirtyDaysAgo);
                const EDate = this.formatDateYYYYMMDD(today);

                const response = await this.$axios.get(`${this.$apiUrls.hanqmedi}/popbill/sms/search`, {
                    params: {
                        CorpNum: this.$globalState.company_bnum,
                        UserID: this.$globalState.popbill_id,
                        SDate,
                        EDate,
                        State: '1,2,3,4', // 전체 상태
                        Item: 'SMS,LMS,MMS', // 전체 타입
                        Page: 1,
                        PerPage: 100, // 충분한 데이터를 가져온 후 필터링
                        Order: 'D' // 내림차순 (최신순)
                    }
                });

                if (response.data && response.data.list) {
                    // 주문서 이름과 정확히 일치하는 항목만 필터링
                    const recipientNameRaw = this.recipient.name?.trim();

                    if (recipientNameRaw) {
                        // recipient.name에서도 괄호 전의 이름 부분만 추출
                        const recipientName = recipientNameRaw.split('(')[0].trim();

                        // receiveName에서 괄호 전의 이름 부분만 추출하여 정확히 일치하는지 확인
                        this.sendHistory = response.data.list.filter(item => {
                            if (!item.receiveName) {
                                return false;
                            }

                            const receiveName = item.receiveName.trim();

                            // 괄호가 있는 경우: "이름(추가정보)" 형식에서 이름 부분만 추출
                            const nameBeforeParenthesis = receiveName.split('(')[0].trim();

                            // 정확히 일치하는 경우만 필터링
                            const isMatch = nameBeforeParenthesis === recipientName;
                            return isMatch;
                        });
                    } else {
                        // recipient.name이 없으면 빈 배열
                        this.sendHistory = [];
                    }
                } else {
                    this.sendHistory = [];
                }
            } catch (error) {
                console.error('전송내역 조회 실패:', error);
                this.sendHistory = [];
                // 에러가 발생해도 모달은 계속 사용 가능하도록 함
            } finally {
                this.isLoadingHistory = false;
            }
        },

        formatDateYYYYMMDD(date) {
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            return `${year}${month}${day}`;
        },

        formatDateTime(dateTimeStr) {
            if (!dateTimeStr) return '-';
            try {
                // yyyyMMddHHmmss 형식으로 오는 경우
                if (dateTimeStr.length === 14) {
                    const year = dateTimeStr.substring(0, 4);
                    const month = dateTimeStr.substring(4, 6);
                    const day = dateTimeStr.substring(6, 8);
                    const hour = dateTimeStr.substring(8, 10);
                    const minute = dateTimeStr.substring(10, 12);
                    const second = dateTimeStr.substring(12, 14);
                    return `${year}-${month}-${day} ${hour}:${minute}:${second}`;
                }
                return dateTimeStr;
            } catch (error) {
                return dateTimeStr;
            }
        },

        getItemTypeLabel(item) {
            const labels = {
                'SMS': '단문',
                'LMS': '장문',
                'MMS': '포토문자'
            };
            return labels[item] || item;
        },

        getStatusLabel(state) {
            const labels = {
                '1': '대기',
                '2': '성공',
                '3': '실패',
                '4': '취소'
            };
            // state가 숫자로 올 수 있으므로 문자열로 변환
            const stateStr = String(state);
            return labels[stateStr] || stateStr;
        },

        getStatusClass(state) {
            const classes = {
                '1': 'status-waiting',
                '2': 'status-success',
                '3': 'status-failed',
                '4': 'status-cancelled'
            };
            // state가 숫자로 올 수 있으므로 문자열로 변환
            const stateStr = String(state);
            return classes[stateStr] || '';
        },

        truncateContent(content) {
            if (!content) return '-';
            return content.length > 50 ? content.substring(0, 50) + '...' : content;
        },

        async sendMessage() {
            try {
                this.$swal.fire({
                    title: '전송 중...',
                    text: '메시지를 전송하고 있습니다.',
                    allowOutsideClick: false,
                    didOpen: () => {
                        this.$swal.showLoading();
                    }
                });

                const sendData = {
                    method: this.sendMethod,
                    recipient: {
                        name: this.recipient.name,
                        phone: this.recipient.phone,
                        companykey: this.recipient.companykey
                    },
                    message: this.message.trim()
                };

                let response;
                if (this.sendMethod === 'kakao') {
                    response = await this.sendKakaoMessage(sendData);
                } else {
                    response = await this.sendSMSMessage(sendData);
                }

                await this.$swal.close();

                if (response && response.success) {
                    this.$swal.fire({
                        title: '전송 완료',
                        text: `${this.sendMethod === 'kakao' ? '알림톡' : '문자메시지'}가 성공적으로 전송되었습니다.`,
                        icon: 'success'
                    });
                    this.$emit('send', sendData);
                    // 전송 후 내역 새로고침
                    this.loadSendHistory();
                } else {
                    throw new Error(response?.message || '메시지 전송에 실패했습니다.');
                }
            } catch (error) {
                await this.$swal.close();
                console.error('메시지 전송 실패:', error);

                this.$swal.fire({
                    title: '전송 실패',
                    text: error.response?.data?.message || '메시지 전송 중 오류가 발생했습니다.',
                    icon: 'error'
                });
            }
        },

        async sendKakaoMessage(sendData) {
            try {
                const templateResponse = await this.$axios.get(`${this.$apiUrls.hanqmedi}/popbill/kakao/listATSTemplate`, {
                    params: {
                        CorpNum: this.$globalState.company_bnum,
                        UserID: this.$globalState.popbill_id
                    }
                });

                if (!templateResponse.data) {
                    throw new Error('알림톡 템플릿을 불러올 수 없습니다.');
                }

                const response = await this.$axios.post(`${this.$apiUrls.hanqmedi}/popbill/kakao/sendFTS`, {
                    CorpNum: this.$globalState.company_bnum,
                    TemplateCode: templateResponse.data[0].templateCode,
                    Sender: this.$globalState.user_phone,
                    SenderName: this.$globalState.user_name,
                    Receiver: sendData.recipient.phone,
                    ReceiverName: sendData.recipient.name,
                    Content: sendData.message,
                    UserID: this.$globalState.popbill_id,
                    PlusFriendID: '@hanqmedi'
                });

                return {
                    success: true,
                    receiptNum: response.data.receiptNum
                };
            } catch (error) {
                console.error('알림톡 전송 실패:', error);
                throw error;
            }
        },

        async sendSMSMessage(sendData) {
            try {
                if (!sendData.recipient.phone) {
                    throw new Error('수신번호가 없습니다.');
                }

                if (!sendData.message) {
                    throw new Error('메시지 내용을 입력해주세요.');
                }

                const response = await this.$axios.post(`${this.$apiUrls.hanqmedi}/popbill/sms/sendSMS`, {
                    CorpNum: this.$globalState.company_bnum,
                    Sender: this.$globalState.user_phone,
                    SenderName: this.$globalState.user_name,
                    Receiver: sendData.recipient.phone,
                    ReceiverName: sendData.recipient.name,
                    Content: sendData.message,
                    UserID: this.$globalState.popbill_id
                });

                return {
                    success: true,
                    receiptNum: response.data.receiptNum
                };
            } catch (error) {
                console.error('SMS 전송 실패:', error);
                throw error;
            }
        },

        closeModal() {
            this.$emit('close');
        },

        applyTemplate(content) {
            if (content.includes('{name}') || content.includes('{company}')) {
                this.message = content
                    .replace('{name}', this.recipient?.name || '')
                    .replace('{company}', this.$globalState?.company || '');
            } else {
                this.message = content;
            }
        }
    }
};
</script>

<style scoped>
.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.6);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
}

.modal-container {
    background: white;
    border-radius: 16px;
    width: 90%;
    max-width: 600px;
    max-height: 85vh;
    overflow: hidden;
    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
    display: flex;
    flex-direction: column;
    animation: slideUp 0.3s ease-out;
}

@keyframes slideUp {
    from {
        transform: translateY(20px);
        opacity: 0;
    }

    to {
        transform: translateY(0);
        opacity: 1;
    }
}

.modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 15px 20px;
    border-bottom: 1px solid #e5e7eb;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
}

.modal-title {
    margin: 0;
    font-size: 1.5rem;
    font-weight: 600;
    display: flex;
    align-items: center;
    gap: 12px;
}

.message-icon {
    font-size: 1.5rem;
}

.close-button {
    background: none;
    border: none;
    color: white;
    cursor: pointer;
    padding: 8px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s;
}

.close-button:hover {
    background: rgba(255, 255, 255, 0.1);
}

.modal-body {
    flex: 1;
    overflow-y: auto;
    padding: 24px 28px;
    display: flex;
    flex-direction: column;
    gap: 24px;

    /* 웹킷 기반 브라우저 스크롤바 스타일링 */
    &::-webkit-scrollbar {
        width: 8px;
    }

    &::-webkit-scrollbar-track {
        background: #f1f5f9;
        border-radius: 10px;
        margin: 4px 0;
    }

    &::-webkit-scrollbar-thumb {
        background: linear-gradient(135deg, #cbd5e1, #94a3b8);
        border-radius: 10px;
        border: 2px solid #f1f5f9;
        transition: all 0.2s ease;

        &:hover {
            background: linear-gradient(135deg, #94a3b8, #64748b);
            transform: scale(1.05);
        }

        &:active {
            background: linear-gradient(135deg, #64748b, #475569);
        }
    }

    &::-webkit-scrollbar-corner {
        background: #f1f5f9;
    }

    /* Firefox 스크롤바 스타일링 */
    scrollbar-width: thin;
    scrollbar-color: #cbd5e1 #f1f5f9;

    /* 스크롤바가 보이지 않을 때도 공간 확보 */
    &::-webkit-scrollbar-track-piece {
        background: transparent;
    }

    /* 스크롤바 버튼 숨기기 (선택사항) */
    &::-webkit-scrollbar-button {
        display: none;
    }
}

.recipient-section {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.section-title {
    margin: 0;
    font-size: 1.1rem;
    font-weight: 600;
    color: #374151;
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.recipient-card {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 16px;
    background: #f8fafc;
    border-radius: 12px;
    border: 1px solid #e2e8f0;
    transition: all 0.2s;
}

.recipient-card:hover {
    border-color: #667eea;
    box-shadow: 0 2px 8px rgba(102, 126, 234, 0.1);
}

.recipient-avatar {
    width: 48px;
    height: 48px;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.5rem;
    box-shadow: 0 2px 8px rgba(102, 126, 234, 0.2);
}

.recipient-info {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.recipient-name {
    font-weight: 600;
    color: #1f2937;
    font-size: 1rem;
}

.recipient-phone {
    color: #6b7280;
    font-size: 0.9rem;
}

.send-method-section {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.method-buttons {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
}

.method-button {
    padding: 16px;
    border: 2px solid #e5e7eb;
    border-radius: 12px;
    background: white;
    cursor: pointer;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    transition: all 0.2s;
}

.method-button:hover {
    border-color: #9ca3af;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.method-button.active {
    border-color: #667eea;
    background: #eff6ff;
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.2);
}

.method-icon {
    font-size: 2rem;
}

.method-text {
    font-weight: 500;
    color: #374151;
}

.history-section {
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 16px;
    background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
    border-radius: 12px;
    border: 1px solid #bae6fd;
}

.history-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.history-button {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    border: none;
    color: white;
    padding: 10px 20px;
    border-radius: 8px;
    cursor: pointer;
    font-weight: 500;
    font-size: 0.9rem;
    display: flex;
    align-items: center;
    gap: 8px;
    transition: all 0.2s;
    box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3);
}

.history-button:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
}

.history-button:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.loading-spinner {
    animation: spin 1s linear infinite;
}

@keyframes spin {
    from {
        transform: rotate(0deg);
    }

    to {
        transform: rotate(360deg);
    }
}

.history-info {
    display: flex;
    align-items: center;
    gap: 8px;
    color: #0369a1;
    font-size: 0.85rem;
}

.info-icon {
    font-size: 1rem;
}

.message-section {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.character-count {
    font-size: 0.85rem;
    color: #6b7280;
    font-weight: normal;
}

.message-textarea {
    min-height: 100px;
    padding: 12px;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    resize: vertical;
    font-family: inherit;
    font-size: 0.9rem;
    line-height: 1.5;
    transition: all 0.2s;
}

.message-textarea:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.message-textarea::placeholder {
    color: #9ca3af;
}

.message-templates {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
}

.template-button {
    background: white;
    border: 1px solid #d1d5db;
    color: #374151;
    padding: 6px 12px;
    border-radius: 6px;
    cursor: pointer;
    font-size: 0.8rem;
    transition: all 0.2s;
}

.template-button:hover {
    background: #f3f4f6;
    border-color: #667eea;
    color: #667eea;
}

.modal-footer {
    padding: 20px 28px;
    border-top: 1px solid #e5e7eb;
    background: #f8fafc;
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.footer-info {
    display: flex;
    align-items: center;
    gap: 8px;
    color: #6b7280;
    font-size: 0.85rem;
}

.footer-buttons {
    display: flex;
    gap: 12px;
}

.cancel-button {
    background: white;
    border: 1px solid #d1d5db;
    color: #374151;
    padding: 10px 20px;
    border-radius: 8px;
    cursor: pointer;
    font-weight: 500;
    transition: all 0.2s;
}

.cancel-button:hover {
    background: #f3f4f6;
}

.send-button {
    background: #667eea;
    border: none;
    color: white;
    padding: 10px 20px;
    border-radius: 8px;
    cursor: pointer;
    font-weight: 500;
    display: flex;
    align-items: center;
    gap: 8px;
    transition: all 0.2s;
}

.send-button:hover:not(:disabled) {
    background: #5a67d8;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
}

.send-button:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
}

.send-button.kakao {
    background: #fee500;
    color: #3c1e1e;
}

.send-button.kakao:hover:not(:disabled) {
    background: #fdd835;
}

.send-button.sms {
    background: #059669;
}

.send-button.sms:hover:not(:disabled) {
    background: #047857;
}

.history-section {
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 16px;
    background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
    border-radius: 12px;
    border: 1px solid #bae6fd;
}

.history-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.history-loading,
.history-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 5px 20px;
    color: #6b7280;
}

.loading-spinner-large {
    font-size: 3rem;
    animation: spin 1s linear infinite;
    margin-bottom: 12px;
}

.empty-icon {
    font-size: 3rem;
    margin-bottom: 12px;
    opacity: 0.5;
}

.history-list {
    display: flex;
    flex-direction: column;
    gap: 12px;
    max-height: 400px;
    overflow-y: auto;

    /* 웹킷 기반 브라우저 스크롤바 스타일링 */
    &::-webkit-scrollbar {
        width: 8px;
    }

    &::-webkit-scrollbar-track {
        background: #f1f5f9;
        border-radius: 10px;
        margin: 4px 0;
    }

    &::-webkit-scrollbar-thumb {
        background: linear-gradient(135deg, #cbd5e1, #94a3b8);
        border-radius: 10px;
        border: 2px solid #f1f5f9;
        transition: all 0.2s ease;

        &:hover {
            background: linear-gradient(135deg, #94a3b8, #64748b);
            transform: scale(1.05);
        }

        &:active {
            background: linear-gradient(135deg, #64748b, #475569);
        }
    }

    &::-webkit-scrollbar-corner {
        background: #f1f5f9;
    }

    /* Firefox 스크롤바 스타일링 */
    scrollbar-width: thin;
    scrollbar-color: #cbd5e1 #f1f5f9;

    /* 스크롤바가 보이지 않을 때도 공간 확보 */
    &::-webkit-scrollbar-track-piece {
        background: transparent;
    }

    /* 스크롤바 버튼 숨기기 (선택사항) */
    &::-webkit-scrollbar-button {
        display: none;
    }
}

.history-item {
    background: white;
    border-radius: 8px;
    padding: 16px;
    border: 1px solid #e2e8f0;
    transition: all 0.2s;
}

.history-item:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    transform: translateY(-1px);
}

.history-item-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;
    padding-bottom: 12px;
    border-bottom: 1px solid #f3f4f6;
}

.history-item-type {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    padding: 4px 12px;
    border-radius: 12px;
    font-size: 0.75rem;
    font-weight: 600;
}

.history-item-status {
    padding: 4px 12px;
    border-radius: 12px;
    font-size: 0.75rem;
    font-weight: 600;
}

.status-waiting {
    background: #fef3c7;
    color: #92400e;
}

.status-success {
    background: #d1fae5;
    color: #065f46;
}

.status-failed {
    background: #fee2e2;
    color: #991b1b;
}

.status-cancelled {
    background: #f3f4f6;
    color: #4b5563;
}

.history-item-body {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.history-item-info {
    display: flex;
    gap: 8px;
    font-size: 0.9rem;
}

.info-label {
    font-weight: 600;
    color: #6b7280;
    min-width: 60px;
}

.info-value {
    color: #1f2937;
    flex: 1;
}

.content-preview {
    color: #4b5563;
    line-height: 1.4;
}
</style>